// hooks/useTranslation.js - Translation hook for Operator App
import React, { useState, useEffect, createContext, useContext } from 'react';
import { translations } from '../translations';

// Create Translation Context
const TranslationContext = createContext();

// Translation Provider Component
export const TranslationProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');

  // Load language from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('operator_language');
    if (savedLanguage && translations[savedLanguage]) {
      setLanguage(savedLanguage);
    } else {
      // Auto-detect browser language if available
      const browserLang = navigator.language.split('-')[0];
      if (translations[browserLang]) {
        setLanguage(browserLang);
      }
    }
  }, []);

  // Translation function
  const t = (key, replacements = {}) => {
    let text = translations[language]?.[key] || translations['en']?.[key] || key;

    // Handle replacements like {0}, {1}, etc.
    if (typeof replacements === 'object' && replacements !== null) {
      Object.keys(replacements).forEach(placeholder => {
        const regex = new RegExp(`\\{${placeholder}\\}`, 'g');
        text = text.replace(regex, replacements[placeholder]);
      });
    }

    return text;
  };

  // Change language function
  const changeLanguage = (newLanguage) => {
    if (translations[newLanguage]) {
      setLanguage(newLanguage);
      localStorage.setItem('operator_language', newLanguage);
    } else {
      console.error('Invalid language:', newLanguage);
    }
  };

  // Get current language info
  const getCurrentLanguageInfo = () => {
    const langMap = {
      'en': { code: 'en', name: 'English', flag: '🇬🇧' },
      'it': { code: 'it', name: 'Italiano', flag: '🇮🇹' }
    };
    return langMap[language] || langMap['en'];
  };

  // Check if key exists
  const hasTranslation = (key) => {
    return !!(translations[language]?.[key] || translations['en']?.[key]);
  };

  const value = {
    language,
    t,
    changeLanguage,
    getCurrentLanguageInfo,
    hasTranslation,
    availableLanguages: Object.keys(translations)
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

// Hook to use translation
export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

export default useTranslation;